import type { ValueOf } from "type-fest"
import type { MerchantBusinessPermissionDetail } from "@/api/user/types/worker"

/**
 * 折扣类型常量映射
 * @constant
 * @readonly
 * @enum {number}
 */
const DISCOUNT_TYPE = {
  /** 整单手动抹零 */
  DISCOUNT_TYPE_MANUAL_ZERO: 11,
  /** 整单手动打折 */
  DISCOUNT_TYPE_MANUAL: 1,
  /** 商品手动打折 */
  DISCOUNT_TYPE_PRODUCT_MANUAL: 12,
  /** 整单手动减免 */
  DISCOUNT_TYPE_MANUAL_DISCOUNT: 2,
  /** 商品手动减免 */
  DISCOUNT_TYPE_PRODUCT_DISCOUNT: 22,
  /** 会员优惠 */
  DISCOUNT_TYPE_MEMBER: 3,
  /** 会员商品优惠 */
  DISCOUNT_TYPE_MEMBER_PRODUCT: 31,
  /** 会员桌台折扣 */
  DISCOUNT_TYPE_MEMBER_TABLE: 32,
  /** 会员服务费折扣 */
  DISCOUNT_TYPE_MEMBER_SERVICE: 33,
  /** 优惠券 */
  DISCOUNT_TYPE_COUPON: 4,
  /** 满减 */
  DISCOUNT_TYPE_FULL_REDUCTION: 5,
  /** 满折 */
  DISCOUNT_TYPE_FULL_DISCOUNT: 6,
  /** 其它折扣 */
  DISCOUNT_TYPE_OTHER: 7,
  /** 单独商品折扣 */
  DISCOUNT_TYPE_ITEM_SPECIFIED_DISCOUNT: 41,
  /** 单独商品减免 */
  DISCOUNT_TYPE_ITEM_SPECIFIED_WAIVER: 42,
  /** 品牌积分兑换商品原价展示，并作为独立商品优惠扣减应收 */
  DISCOUNT_TYPE_BRAND_POINT_ITEM_REDEMPTION: 71,
  /** 品牌积分抵扣订单金额 */
  DISCOUNT_TYPE_BRAND_POINT_ORDER_DEDUCTION: 72,
} as const

type DISCOUNT_TYPE_VALUE = ValueOf<typeof DISCOUNT_TYPE>

/**
 * 获取折扣类型对应的折扣对象
 * @param type 折扣类型
 * @returns 折扣对象
 */
function getDiscountFor(type: DISCOUNT_TYPE_VALUE): 'order' | 'item' | 'unknown' {
  const orderIgnreList: DISCOUNT_TYPE_VALUE[] = [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL, DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_DISCOUNT, DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_ZERO]
  const itemIgnoreList: DISCOUNT_TYPE_VALUE[] = [DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_MANUAL, DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_DISCOUNT]
  return orderIgnreList.includes(type) ? 'order' : (itemIgnoreList.includes(type) ? 'item' : 'unknown')
}

/**
 * 获取折扣类型对应的折扣类型
 * @param type 折扣类型
 * @returns 折扣类型
 */
function getDiscountType(type: DISCOUNT_TYPE_VALUE): 'discount' | 'waiver' | 'zero' | 'unknown' {
  const discountList: DISCOUNT_TYPE_VALUE[] = [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL, DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_MANUAL]
  const waiverList: DISCOUNT_TYPE_VALUE[] = [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_DISCOUNT, DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_DISCOUNT]
  const zeroList: DISCOUNT_TYPE_VALUE[] = [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_ZERO]
  return discountList.includes(type) ? 'discount' : (waiverList.includes(type) ? 'waiver' : (zeroList.includes(type) ? 'zero' : 'unknown'))
}

/**
 * 折扣类型中文描述映射
 */
const DISCOUNT_TYPE_LABEL: Record<DISCOUNT_TYPE_VALUE, string> = {
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_ZERO]: '整单手动抹零',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL]: '整单手动打折',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_MANUAL]: '商品手动打折',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_DISCOUNT]: '整单手动减免',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_DISCOUNT]: '商品手动减免',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER]: '会员优惠',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_PRODUCT]: '会员商品优惠',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_TABLE]: '会员桌台折扣',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_SERVICE]: '会员服务费折扣',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_COUPON]: '优惠券',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_FULL_REDUCTION]: '满减',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_FULL_DISCOUNT]: '满折',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_OTHER]: '其它折扣',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_ITEM_SPECIFIED_DISCOUNT]: '单独商品折扣',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_ITEM_SPECIFIED_WAIVER]: '单独商品减免',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_BRAND_POINT_ITEM_REDEMPTION]: '积分兑商品',
  [DISCOUNT_TYPE.DISCOUNT_TYPE_BRAND_POINT_ORDER_DEDUCTION]: '积分抵现',
} as const

/**
 * 获取折扣类型的中文描述
 * @param type - 折扣类型值
 * @returns 折扣类型的中文描述，如果类型不存在则返回'未知折扣类型'
 */
function getDiscountTypeLabel(type: DISCOUNT_TYPE_VALUE): string {
  return DISCOUNT_TYPE_LABEL[type] ?? '未知折扣类型'
}

/**
 * 折扣类型业务权限映射
 */
const DISCOUNT_TYPE_PERMISSION_MAP: Record<DISCOUNT_TYPE_VALUE, Array<keyof MerchantBusinessPermissionDetail>> = {
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_ZERO]: ['manualZeroLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL]: ['orderDiscountLimit', 'orderWaiverLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_MANUAL]: ['itemDiscountLimit', 'itemWaiverLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MANUAL_DISCOUNT]: ['orderWaiverLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_PRODUCT_DISCOUNT]: ['itemWaiverLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_PRODUCT]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_TABLE]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_SERVICE]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_COUPON]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_FULL_REDUCTION]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_FULL_DISCOUNT]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_OTHER]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_ITEM_SPECIFIED_DISCOUNT]: ['itemDiscountLimit', 'itemWaiverLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_ITEM_SPECIFIED_WAIVER]: ['itemWaiverLimit'],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_BRAND_POINT_ITEM_REDEMPTION]: [],
  [DISCOUNT_TYPE.DISCOUNT_TYPE_BRAND_POINT_ORDER_DEDUCTION]: [],
} as const

/**
 * 获取对应折扣类型所需要的检查的业务权限列表
 */
function getDiscountCheckList(type: DISCOUNT_TYPE_VALUE): Array<keyof MerchantBusinessPermissionDetail> {
  return DISCOUNT_TYPE_PERMISSION_MAP[type] ?? []
}

function getDiscountOrWaiver(type: keyof MerchantBusinessPermissionDetail): 'waiver' | 'discount' | 'unknown' {
  const waiverList = ['itemWaiverLimit', 'orderWaiverLimit', 'manualZeroLimit', 'freeOrderLimit']
  const discountList = ['itemDiscountLimit', 'orderDiscountLimit']
  return waiverList.includes(type) ? 'waiver' : (discountList.includes(type) ? 'discount' : 'unknown')
}

export function isMemberDiscount(discountType: DISCOUNT_TYPE_VALUE) {
  const memberDiscountList = [DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER, DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_PRODUCT, DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_TABLE, DISCOUNT_TYPE.DISCOUNT_TYPE_MEMBER_SERVICE] as DISCOUNT_TYPE_VALUE[]
  return memberDiscountList.includes(discountType)
}

export { DISCOUNT_TYPE, DISCOUNT_TYPE_LABEL, getDiscountCheckList, getDiscountFor, getDiscountOrWaiver, getDiscountType, getDiscountTypeLabel }
export type { DISCOUNT_TYPE_VALUE }
